"use client";

import * as React from "react";

type Source = {
  id: string;
  url: string;
  title: string | null;
  active: boolean;
  checkMode: "HTML_TEXT" | "RAW_HTML";
  analysisPriority: number;
  checkIntervalMonths: number;
  nextCheckAt: string | null;
  lastCheckedAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
};

type Change = {
  id: string;
  sourceId: string;
  sourceTitle: string | null;
  sourceUrl: string;
  changeType: "INITIAL" | "CHANGED";
  summary: string | null;
  createdAt: string;
};

export default function LegalMonitorScreen() {
  const [sources, setSources] = React.useState<Source[]>([]);
  const [changes, setChanges] = React.useState<Change[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [url, setUrl] = React.useState("https://www.fedlex.admin.ch/eli/cc/2009/615/de");
  const [title, setTitle] = React.useState("Quelle 1: MWSTG (Fedlex)");
  const [analysisPriority, setAnalysisPriority] = React.useState("100");
  const [note, setNote] = React.useState<string | null>(null);
  const [snapshotText, setSnapshotText] = React.useState<string | null>(null);
  const [snapshotTitle, setSnapshotTitle] = React.useState<string>("Gesetzestext");

  async function load() {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/legal-monitor/sources", { cache: "no-store" });
      const data = (await res.json()) as { ok?: boolean; sources?: Source[]; changes?: Change[]; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "Konnte Quellen nicht laden");
      setSources(data.sources || []);
      setChanges(data.changes || []);
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Laden fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => {
    load();
  }, []);

  async function addSource() {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/legal-monitor/sources", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          title,
          checkMode: "HTML_TEXT",
          active: true,
          checkIntervalMonths: 1,
          analysisPriority: Number(analysisPriority) || 100
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "Quelle konnte nicht gespeichert werden");
      setNote("✅ Quelle gespeichert.");
      await load();
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  async function runCheck(sourceId?: string) {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/legal-monitor/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceId })
      });
      const data = (await res.json()) as {
        ok?: boolean;
        result?: { checked: number; changed: number; failed: number };
        error?: string;
      };
      if (!res.ok || !data.ok) throw new Error(data.error || "Check fehlgeschlagen");
      const r = data.result || { checked: 0, changed: 0, failed: 0 };
      setNote(`✅ Check fertig: geprüft ${r.checked}, geändert ${r.changed}, Fehler ${r.failed}.`);
      await load();
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Check fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  async function openLatestText(source: Source) {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/legal-monitor/snapshots/latest?sourceId=${encodeURIComponent(source.id)}`, {
        cache: "no-store"
      });
      const data = (await res.json()) as {
        ok?: boolean;
        snapshot?: { checkedAt: string; text: string } | null;
        error?: string;
      };
      if (!res.ok || !data.ok) throw new Error(data.error || "Snapshot konnte nicht geladen werden");
      if (!data.snapshot) {
        throw new Error("Kein Snapshot vorhanden. Bitte zuerst 'Jetzt prüfen' ausführen.");
      }
      setSnapshotTitle(`${source.title || source.url} · ${new Date(data.snapshot.checkedAt).toLocaleString("de-CH")}`);
      setSnapshotText(data.snapshot.text || "(leer)");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Snapshot konnte nicht geladen werden");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Legal Monitor</h1>
          <p>Überwacht Gesetzesquellen (z. B. Fedlex) und meldet Textänderungen mit Historie.</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => runCheck()}>
            {busy ? "Läuft..." : "Alle Quellen prüfen"}
          </button>
        </div>
      </div>

      <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 220px 120px 160px", gap: 10 }}>
          <input
            className="input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            disabled={busy}
          />
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Quellenname (z. B. Quelle 2: ESTV Wegleitung)"
            disabled={busy}
          />
          <input
            className="input"
            value={analysisPriority}
            onChange={(e) => setAnalysisPriority(e.target.value)}
            placeholder="Priorität"
            title="Niedriger Wert = höhere Priorität"
            disabled={busy}
          />
          <button className="secondary-btn" type="button" onClick={addSource} disabled={busy}>
            Quelle speichern
          </button>
        </div>
      </div>

      <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Quellen</div>
        {sources.length === 0 ? (
          <div style={{ color: "var(--muted)" }}>Noch keine Quellen vorhanden.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Titel</th>
                <th>URL</th>
                <th>Status</th>
                <th>Prio</th>
                <th>Intervall</th>
                <th>Nächster Check</th>
                <th>Letzter Check</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id}>
                  <td>{s.title || "-"}</td>
                  <td style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.url}
                  </td>
                  <td>{s.lastStatus || "-"}</td>
                  <td>{s.analysisPriority}</td>
                  <td>{s.checkIntervalMonths} Monat(e)</td>
                  <td>{s.nextCheckAt ? new Date(s.nextCheckAt).toLocaleString("de-CH") : "-"}</td>
                  <td>{s.lastCheckedAt ? new Date(s.lastCheckedAt).toLocaleString("de-CH") : "-"}</td>
                  <td style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost" type="button" onClick={() => runCheck(s.id)} disabled={busy}>
                      Jetzt prüfen
                    </button>
                    <button className="btn btn-ghost" type="button" onClick={() => openLatestText(s)} disabled={busy}>
                      Text öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-wrap" style={{ padding: 14 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Letzte Änderungen</div>
        {changes.length === 0 ? (
          <div style={{ color: "var(--muted)" }}>Noch keine Änderungen protokolliert.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Quelle</th>
                <th>Typ</th>
                <th>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.id}>
                  <td>{new Date(c.createdAt).toLocaleString("de-CH")}</td>
                  <td>{c.sourceTitle || c.sourceUrl}</td>
                  <td>
                    <span className={c.changeType === "CHANGED" ? "pill pill-provisional" : "pill pill-review"}>
                      {c.changeType}
                    </span>
                  </td>
                  <td>{c.summary || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {note && <div style={{ marginTop: 10, color: "var(--muted)" }}>{note}</div>}

      {snapshotText !== null && (
        <div className="doc-modal-overlay" onClick={() => setSnapshotText(null)}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="doc-modal-head">
              <div>
                <div className="doc-modal-title">Snapshot-Text</div>
                <div className="doc-modal-sub">{snapshotTitle}</div>
              </div>
              <button className="secondary-btn" type="button" onClick={() => setSnapshotText(null)}>
                Schliessen
              </button>
            </div>
            <div className="doc-modal-body" style={{ padding: 12, overflow: "auto", background: "#fff" }}>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0, color: "#0e1633", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}>
                {snapshotText}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
